import { child, set } from "firebase/database"

export default defineNuxtPlugin((nuxtApp) => {


    const jobV1Store = usePosJobsStore()
    const tablesStateStore = usePosTableStatesStore()
    const shopStore = usePosShopStore()

    // tables_v3 is a Main-Cashier-only publish: the owner dashboard reads it,
    // and a terminal / 2nd cashier / readonly device publishing too would race
    // the real cashier with N identical writes per table change. Same
    // write-time gate as mobile-v1.sync.plugin. "_" is the pre-bootstrap
    // storeId default (useRealtimeDatabase) — writing there litters a junk
    // node shared across every store (cloud.mobile-v2-history.plugin pattern).
    const canPublish = () =>
        shopStore.runMode === "cashier" &&
        !!shopStore.storeId && String(shopStore.storeId) !== "_"


    const previousFingerprints = ref<Record<string, string>>({})
    // {
    //     "{table}": "<serialized table>",
    // }

    // First run is tracked with an explicit flag, NOT `Object.keys(previous).length
    // === 0`: an empty shop legitimately produces zero fingerprints, and the old
    // check re-armed the first-run branch on every such tick — re-issuing a
    // whole-node set() forever.
    let firstRun = true

    // The fingerprint is the serialized table itself. It used to be
    // `${items.length}-${grandTotal}`, which silently dropped every change that
    // moved neither count nor total: a line comment, a per-line discount that nets
    // to the same total, a split-bill reassignment, and — because guests/setting
    // live in tables_state — a guest-count change. Consumers (owner mobile
    // dashboard, and the mobile app's live table view) then showed stale data with
    // no later publish to repair it.
    function buildFingerprint(table: any): string {
        if (!table) return ''
        return JSON.stringify(table)
    }

    watch([() => jobV1Store.tables, () => tablesStateStore.tablesState],
        ([tables, tablesState]) => {

            // Guard BEFORE any fingerprint bookkeeping: a blocked tick must not
            // clear firstRun / seed previousFingerprints, so when runMode arrives
            // mid-boot the first allowed tick still runs the whole-node repair.
            if (!canPublish()) return

            const currentFingerprints: Record<string, string> = {}

            // build fingerprints for all current tables
            const _tables = tables as Record<string, any>
            for (const tableName in _tables) {
                currentFingerprints[tableName] = buildFingerprint(_tables[tableName])
            }

            // On first run, snapshot the fingerprints and replace the whole node.
            // Kept deliberately: this is ONE set() on tables_v3, not a write per
            // table, so a reload costs a single request and it doubles as a repair
            // for a node that drifted while the POS was closed. (The mobile-v1
            // plugin can NOT do this — there each table is its own node, so the
            // same idea would cost 2 writes × table count. It seeds instead.)
            if (firstRun) {
                firstRun = false
                previousFingerprints.value = { ...currentFingerprints }
                updateAllTables(tables)
                return
            }

            // compare previous vs current fingerprints, table by table
            for (const tableName in currentFingerprints) {
                const prev = previousFingerprints.value[tableName]
                const curr = currentFingerprints[tableName]

                if (prev !== curr) {
                    // Fingerprints are full serialized tables now — log the fact, not the blob.
                    console.log(`[cloud.tables-state] table "${tableName}" changed`)
                    updateTableState(tableName, _tables[tableName])
                }
            }

            // detect removed tables
            for (const tableName in previousFingerprints.value) {
                if (!(tableName in currentFingerprints)) {
                    console.log(`[cloud.tables-state] table "${tableName}" removed`)
                    updateTableState(tableName, null)
                }
            }

            // save current as previous for next comparison
            previousFingerprints.value = { ...currentFingerprints }

        }, { deep: true })



    function updateTableState(tableName: string, table: any) {
        const v3Ref = useRealtimeDatabase().tablesV3Ref()
        const tableRef = child(v3Ref, tableName)
        set(tableRef, table || null)
    }

    function updateAllTables(tables: any) {
        if (!tables) return
        const v3Ref = useRealtimeDatabase().tablesV3Ref()
        set(v3Ref, tables)
    }
})